import React, { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Input, Modal, Space, Spin, Typography } from 'antd';
import { useTranslate } from '@/translations';
import {
  useCreateInspectionsVehiclesNotes,
  useInspectionsVehiclesNotes,
} from '@/api/Inspections';
import { CustomCard, Icon, faPlus } from '@/ui/components';
import { Note } from '@/types';
import { timeConverter } from '@/utils/helpers';

export const Notes: FC<{ canEditInspection?: boolean }> = ({
  canEditInspection,
}) => {
  const { id } = useParams();
  const inspectionId = Number(id);
  const { t } = useTranslate();
  const [comment, setComment] = useState<string>();
  const { isFetching, data } = useInspectionsVehiclesNotes(inspectionId);
  const { mutate: createNote, isPending: creating } =
    useCreateInspectionsVehiclesNotes(inspectionId);
  const notes = data?.entities ?? [];

  const close = () => {
    if (!creating) setComment(undefined);
  };

  const save = () => {
    const value = comment?.trim() ?? '';
    if (!value) return;
    createNote(value, { onSuccess: () => setComment(undefined) });
  };

  return (
    <Spin spinning={isFetching}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div className="fw-semibold">{t('inspectionNotes')}</div>
        {canEditInspection ? (
          <Button
            type="text"
            aria-label={t('addNote')}
            icon={<Icon icon={faPlus} />}
            onClick={() => setComment('')}
          />
        ) : null}
      </div>
      <CustomCard>
        {notes.length === 0 ? (
          <Typography.Text disabled>{t('noNotes')}</Typography.Text>
        ) : (
          <Space direction="vertical" size="middle">
            {notes.map((note) => (
              <NoteRow key={note.id} note={note} />
            ))}
          </Space>
        )}
      </CustomCard>
      <Modal
        open={comment !== undefined}
        title={t('addNote')}
        okText={t('save')}
        confirmLoading={creating}
        okButtonProps={{ disabled: !comment?.trim() }}
        onOk={save}
        onCancel={close}
        destroyOnHidden
      >
        <Input.TextArea
          rows={5}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </Modal>
    </Spin>
  );
};

const NoteRow: FC<{ note: Note }> = ({ note }) => (
  <div>
    {note.username || note.commentDateTime ? (
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {[
          note.username,
          timeConverter(note.commentDateTime ?? undefined, 'lll'),
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography.Text>
    ) : null}
    <Typography.Paragraph className="mb-1">{note.comment}</Typography.Paragraph>
  </div>
);
